/**
 * Website Router - Public website routes only (no admin)
 *
 * React Router v6: layouts use <Outlet />; image/.img and nested page paths are
 * dispatched from a splat route because RR6 dropped regex path params.
 */
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom'
import { useAppContext } from '@/context/AppContext'
import { APPEARANCE_PREVIEW_QUERY } from '@/theme/tokens'
import { resolveInheritedThemeColor } from '@/utils/pagePaths'
import { RealPathnameContext } from './realPathname'

import WebsiteLayout from '@app/(website)/layout'
import HomePage from '@app/(website)/page'
import ResourcesPage from '@app/(website)/resources/page'
import ResourcesLoginPage from '@app/(website)/resources/login/page'
import PublicImageRoute from '@app/(website)/routes/PublicImageRoute'
import PublicVideoRoute from '@app/(website)/routes/PublicVideoRoute'
import PublicPageRoute from '@app/(website)/routes/PublicPageRoute'
import PageRedirect from '@app/(website)/routes/PageRedirect'

/**
 * Catch-all for public paths: *.img / *.vid media URLs, parent redirects, or pages.
 */
function PublicPathDispatcher() {
    const location = useLocation()
    const params = useParams()
    const splat = params['*'] || ''
    const pathname = `/${splat}`.replace(/\/+/g, '/').replace(/\/$/, '') || '/'

    const videoMatch = pathname.match(/^\/(?:(.+)\/)?([^/]+)\.vid$/)
    if (videoMatch) {
        const slug = videoMatch[1] || null
        const video = videoMatch[2]
        const match = {
            params: { slug, video },
            path: pathname,
            url: pathname,
            isExact: true,
        }
        return <PublicVideoRoute match={match} location={location} />
    }

    const imageMatch = pathname.match(/^\/(?:(.+)\/)?([^/]+)\.img$/)
    if (imageMatch) {
        const slug = imageMatch[1] || null
        const image = imageMatch[2]
        const match = {
            params: { slug, image },
            path: pathname,
            url: pathname,
            isExact: true,
        }
        return <PublicImageRoute match={match} location={location} />
    }

    const slug = splat
    const match = {
        params: { slug },
        path: pathname,
        url: pathname,
        isExact: true,
    }

    // Single-segment slugs may need a parent redirect; nested go straight to page
    if (slug && !slug.includes('/')) {
        return <PageRedirect match={match} location={location} />
    }
    return <PublicPageRoute match={match} location={location} />
}
import { viewerBackground } from './viewerBackground'

const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

const VIEWER_THEME_VARS = [
    ['--color-background', 'themeBackground'],
    ['--color-text', 'themeText'],
]

function MediaLayer({ tileAttr, tileId, page }) {
    const { content } = useAppContext()
    const pages = content?.pages

    // The layout styles the root for the page underneath; the viewer wears the
    // open page's colours (a mobile story is portalled to body, so set them on
    // the root). Layout effect: apply before paint so the theme does not flash.
    useIsoLayoutEffect(() => {
        const root = document.documentElement
        const prev = VIEWER_THEME_VARS.map(([name]) => [name, root.style.getPropertyValue(name)])
        VIEWER_THEME_VARS.forEach(([name, key]) => {
            const value = resolveInheritedThemeColor(page, pages, key)
            if (value) root.style.setProperty(name, value)
            else root.style.removeProperty(name)
        })
        return () => {
            prev.forEach(([name, value]) => {
                if (value) root.style.setProperty(name, value)
                else root.style.removeProperty(name)
            })
        }
    }, [page, pages])

    // The page underneath must not scroll or take focus while covered.
    useIsoLayoutEffect(() => {
        const html = document.documentElement
        const app = document.getElementById('app-container')
        const prev = { overflow: html.style.overflow, gutter: html.style.scrollbarGutter }
        html.style.scrollbarGutter = 'stable'
        html.style.overflow = 'hidden'
        app?.setAttribute('inert', '')
        return () => {
            html.style.overflow = prev.overflow
            html.style.scrollbarGutter = prev.gutter
            app?.removeAttribute('inert')
        }
    }, [])

    // Follow the viewer: when the open item's tile is off screen, centre it,
    // so closing reveals the page exactly where it belongs.
    useIsoLayoutEffect(() => {
        const tile = document.querySelector(`#app-container [${tileAttr}="${CSS.escape(tileId)}"]`)
        if (!tile) return
        const rect = tile.getBoundingClientRect()
        if (rect.top >= 0 && rect.bottom <= window.innerHeight) return
        const top = window.scrollY + rect.top + rect.height / 2 - window.innerHeight / 2
        window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'instant' })
    }, [tileAttr, tileId])

    // Stacks above the page's own fixed chrome without catching input itself;
    // the viewers are fixed and take input through the `contents` child.
    return (
        <div className="pointer-events-none fixed inset-0 z-[10000]">
            <div className="pointer-events-auto contents">
                <Routes>
                    <Route path="*" element={<PublicPathDispatcher />} />
                </Routes>
            </div>
        </div>
    )
}

MediaLayer.propTypes = {
    tileAttr: PropTypes.string.isRequired,
    tileId: PropTypes.string.isRequired,
    page: PropTypes.object,
}

function WebsiteRouter() {
    const location = useLocation()
    const { content } = useAppContext()
    // Latched: soft navigation drops the query from the SPA URL.
    const [editorFrame] = useState(() => (
        new URLSearchParams(location.search || '').get(APPEARANCE_PREVIEW_QUERY) === '1'
    ))
    const background = useMemo(
        () => viewerBackground(location.pathname, content, { editorFrame }),
        [location.pathname, content, editorFrame],
    )
    const backgroundPath = background?.pathname || null
    // The query stays: the layout reads ?appearancePreview from it on first load.
    const pageLocation = useMemo(
        () => (backgroundPath
            ? { pathname: backgroundPath, search: location.search, hash: '', state: null, key: `media:${backgroundPath}` }
            : null),
        [backgroundPath, location.search],
    )

    return (
        <RealPathnameContext.Provider value={location.pathname}>
            <Routes location={pageLocation || location}>
                <Route path="/" element={<WebsiteLayout />}>
                    <Route index element={<HomePage />} />
                    <Route path="resources/login" element={<ResourcesLoginPage />} />
                    <Route path="resources" element={<ResourcesPage />} />
                    <Route path="*" element={<PublicPathDispatcher />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {background && (
                <MediaLayer
                    tileAttr={background.tileAttr}
                    tileId={background.tileId}
                    page={background.page}
                />
            )}
        </RealPathnameContext.Provider>
    )
}

export default WebsiteRouter
